import React, {useCallback, useEffect, useRef, useState} from 'react';
import noop from 'lodash/noop';
import I18n from 'app/i18n';
import Get from 'app/utils/Get';
import Post from 'app/utils/Post';
import Delete from 'app/utils/Delete';
import {PopupDialog} from 'app/utils/modals/PopupDialog';
import {showPopup} from 'app/utils/modals/ModalsService';
import {preventEscPopupClose, enableEscPopupClose} from 'app/utils/modals/popups';
import {modalsStore, userStore} from 'app/store/global';
import {startGoogleAuthFlow} from 'app/utils/googleAuth';

import {startSteamAuthFlow} from 'app/utils/steamLink';
import {startBattlenetAuthFlow} from 'app/utils/battlenetAuth';
import {ESC_KEY_CODE} from 'app/utils/Consts';
import {RowCountryCode} from 'app/components/auth/rows/RowCountryCode';

const POPUP_ID = 'external-registration';
const PENDING_META_SELECTOR = 'meta[name="external-registration-pending"][content="true"]';
const registrationUrl = (): string => `/${I18n.locale}/registration/complete.json`;

const AUTH_PROVIDERS = [
    {id: 'google', label: 'Google', start: (): void => startGoogleAuthFlow()},
    {id: 'steam', label: 'Steam', start: (): void => startSteamAuthFlow()},
    {id: 'battlenet', label: 'Battle.net', start: (): void => startBattlenetAuthFlow()}
];
const EMAIL_PROVIDERS = ['steam', 'battlenet'];

const requiresEmail = (provider: string): boolean => EMAIL_PROVIDERS.includes(provider);

const introKey = (provider: string, key: string): string => `external_registration.${
    requiresEmail(provider) ? `email_${key}` : key}`;

const renderRegistrationError = (error: string): React.ReactElement => <div key={error}>{error}</div>;

export const ExternalRegistrationPopup = ({popupId}: {popupId: string}): React.ReactElement => {
    const nickname = useRef<HTMLInputElement>(null);
    const inFlight = useRef(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [expired, setExpired] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [provider, setProvider] = useState<string>(null);

    const abandon = useCallback((): void => {
        document.querySelector(PENDING_META_SELECTOR)?.remove();
        new Delete({url: registrationUrl()}).execute().catch(() => null);
    }, []);

    const cancel = useCallback((): void => {
        abandon();
        modalsStore.hideModal(popupId);
    }, [abandon, popupId]);

    const finishSubmit = useCallback((): void => {
        inFlight.current = false;
        setSubmitting(false);
    }, []);

    const handleResponse = useCallback((data: {
        success?: boolean;
        redirect_url?: string;
        expired?: boolean;
        errors?: string[];
    }): void => {
        if (data.success) {
            window.location.assign(data.redirect_url);
            return;
        }
        setExpired(Boolean(data.expired));
        setErrors(data.errors || [I18n.t('external_registration.failed')]);
        finishSubmit();
    }, [finishSubmit]);

    const submit = useCallback((event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (inFlight.current || !provider) {
            return;
        }

        const form = new FormData(event.currentTarget);
        const countryCode = form.get('country_code');

        if (!countryCode) {
            setErrors([I18n.t('select_a_country')]);
            return;
        }
        inFlight.current = true;
        setSubmitting(true);
        setErrors([]);
        const params = {
            country_code: countryCode,
            nickname: nickname.current.value,
            ...requiresEmail(provider) ? {email: form.get('email')} : {}
        };

        new Post({params, url: registrationUrl()}).execute()
            .then(response => response.json())
            .then(handleResponse)
            .catch(() => {
                setErrors([I18n.t('external_registration.failed')]);
                finishSubmit();
            });
    }, [
        finishSubmit,
        handleResponse,
        provider
    ]);

    useEffect(() => {
        preventEscPopupClose();
        new Get({url: registrationUrl()}).execute()
            .then(response => response.json())
            .then(data => {
                setExpired(Boolean(data.expired));
                setProvider(data.provider);
                setLoadFailed(!data.expired && !data.provider);
            })
            .catch(() => setLoadFailed(true));
        return () => {
            enableEscPopupClose();
        };
    }, []);

    useEffect(() => {
        const onKeyUp = (event: KeyboardEvent): void => {
            const closesCountryList = event.target instanceof Element && Boolean(event.target.closest('.chosen-container'));

            if (event.key === ESC_KEY_CODE && !closesCountryList && modalsStore.lastModal()?.id === popupId) {
                cancel();
            }
        };

        document.addEventListener('keyup', onKeyUp);
        return () => document.removeEventListener('keyup', onKeyUp);
    }, [cancel, popupId]);

    return <PopupDialog id={popupId} setFocus onClose={abandon}>
        <div className="popup__content popup__content--small" role="dialog" aria-modal="true"
            aria-labelledby="external-registration-title">
            <h2 id="external-registration-title">
                {I18n.t(expired || loadFailed ? 'external_registration.expired_title' : introKey(provider, 'title'))}
            </h2>
            {expired || loadFailed
                ? <>
                    <p>{I18n.t(`external_registration.${expired ? 'expired' : 'failed'}`)}</p>
                    <div className="popup-form__submit-row popup-form__submit-row--pair">
                        {AUTH_PROVIDERS.filter(({id}) => !provider || provider === id).map(({id, label, start}) => <button
                            key={id} type="button" className="btn btn--large" onClick={start}>{label}</button>)}
                    </div>
                </>
                : <form className={`popup-form${errors.length > 0 ? ' popup-form--error' : ''}`} onSubmit={submit}>
                    <p>{I18n.t(introKey(provider, 'description'))}</p>
                    {errors.length > 0 && <div className="popup-form__error-hint" role="alert">
                        {errors.map(renderRegistrationError)}
                    </div>}
                    <div className="popup-form__row">
                        <label htmlFor="external-registration-nickname">{I18n.t('external_registration.nickname')}</label>
                        <div className={`field${errors.length > 0 ? ' field-error' : ''}`}>
                            <input ref={nickname} id="external-registration-nickname" name="nickname"
                                className="field__input" type="text" required minLength={4} maxLength={32}
                                autoComplete="nickname" aria-describedby="external-registration-hint"/>
                        </div>
                        <p id="external-registration-hint" className="popup-form__hint">
                            {I18n.t('external_registration.hint')}
                        </p>
                    </div>
                    {requiresEmail(provider) && <div className="popup-form__row">
                        <label htmlFor="external-registration-email">{I18n.t('email')}</label>
                        <div className={`field${errors.length > 0 ? ' field-error' : ''}`}>
                            <input id="external-registration-email" name="email"
                                className="field__input" type="email" required maxLength={254} autoComplete="email"
                                aria-describedby="external-registration-email-hint"/>
                        </div>
                        <p id="external-registration-email-hint" className="popup-form__hint">
                            {I18n.t('external_registration.email_hint')}
                        </p>
                    </div>}
                    <RowCountryCode rowClass="popup-form__row" errorHint={null} country_code=""
                        cleanError={noop}/>
                    <div className="popup-form__submit-row popup-form__submit-row--pair">
                        <button className="btn btn--large" type="submit" disabled={submitting || !provider}>
                            {I18n.t('external_registration.submit')}
                        </button>
                        <button className="btn btn--large btn--invert" type="button" onClick={cancel}>
                            {I18n.t('external_registration.cancel')}
                        </button>
                    </div>
                </form>}
        </div>
    </PopupDialog>;
};

const renderExternalRegistrationPopup = function(props: {popupId: string}): React.ReactElement {
    return <ExternalRegistrationPopup popupId={props.popupId}/>;
};

export const showExternalRegistration = (): boolean => {
    if (userStore.user.user_id || !document.querySelector(PENDING_META_SELECTOR)) {
        return false;
    }
    preventEscPopupClose();
    if (!modalsStore.isModalOpen(POPUP_ID)) {
        showPopup(POPUP_ID, renderExternalRegistrationPopup);
    }
    return true;
};
